import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import {
  ArrowRight, BadgeCheck, CalendarRange, Check, Clock3, Loader2,
  MessageCircle, Printer, ShieldCheck, Ticket, TriangleAlert,
} from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { SOCIALS } from "@/constants/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const PLAN_LABELS = {
  advance: "Reserve with Advance",
  full: "Full Payment",
};

const fireConfetti = () => {
  const colors = ["#0F2A43", "#4F6D8A", "#8FAECC", "#F8F6F2"];
  confetti({ particleCount: 140, spread: 80, origin: { y: 0.25 }, colors, disableForReducedMotion: true });
  setTimeout(() => {
    confetti({ particleCount: 70, angle: 60, spread: 60, origin: { x: 0, y: 0.4 }, colors, disableForReducedMotion: true });
    confetti({ particleCount: 70, angle: 120, spread: 60, origin: { x: 1, y: 0.4 }, colors, disableForReducedMotion: true });
  }, 350);
};

const Row = ({ label, value, mono = false, testid }) => (
  <div className="flex items-center justify-between gap-6 py-3.5 border-b border-dashed border-[#E5E7EB] last:border-0">
    <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#6B7280] shrink-0">{label}</span>
    <span data-testid={testid} className={`text-right text-sm font-semibold text-[#111827] ${mono ? "font-mono tracking-wide" : ""}`}>
      {value}
    </span>
  </div>
);

export default function BookingSuccessPage() {
  const [params] = useSearchParams();
  const bid = params.get("bid") || "";
  const [booking, setBooking] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [waHref, setWaHref] = useState(SOCIALS.instagram);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!bid) {
      setState("error");
      return;
    }
    axios
      .get(`${API}/workshop-bookings/${encodeURIComponent(bid)}`)
      .then((r) => {
        setBooking(r.data);
        setState("ready");
        if (r.data.status === "paid") fireConfetti();
      })
      .catch(() => setState("error"));
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data.whatsapp_number) {
          setWaHref(
            `https://wa.me/${r.data.whatsapp_number}?text=${encodeURIComponent(`Hi Akhilesh, meri DJI Avata 2 FPV Masterclass booking confirm ho gayi. Booking ID: ${bid}`)}`
          );
        }
      })
      .catch(() => {});
  }, [bid]);

  const paid = booking?.status === "paid";

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="relative bg-[#F8F6F2] text-[#111827] min-h-screen"
      data-testid="booking-success-page"
    >
      <div className="print:hidden">
        <AeroNav />
      </div>

      <section className="max-w-[1320px] mx-auto px-6 md:px-10 pt-36 pb-24 md:pt-40">
        {state === "loading" && (
          <div className="flex flex-col items-center justify-center py-32" data-testid="bs-loading">
            <Loader2 size={30} className="animate-spin text-[#4F6D8A]" />
            <p className="mt-5 font-mono text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">Confirming your booking…</p>
          </div>
        )}

        {state === "error" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="max-w-xl mx-auto bg-white rounded-[28px] border border-[#E5E7EB] shadow-[0_35px_80px_-30px_rgba(15,42,67,0.3)] p-10 md:p-14 text-center"
            data-testid="bs-error"
          >
            <span className="mx-auto w-16 h-16 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
              <TriangleAlert size={26} className="text-amber-600" />
            </span>
            <h1 className="mt-7 font-serif text-3xl md:text-4xl text-[#111827]">Booking nahi mila</h1>
            <p className="mt-4 text-[#6B7280] leading-relaxed">
              Yeh confirmation link invalid ya expire ho sakta hai. Agar payment ho gaya tha, toh fikar mat karo — WhatsApp par booking ID bhej do, main confirm kar dunga.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link
                to="/book-workshop"
                data-testid="bs-error-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#4F6D8A] hover:-translate-y-0.5 transition-all duration-300"
              >
                Book Again <ArrowRight size={13} />
              </Link>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bs-error-whatsapp"
                className="inline-flex items-center justify-center gap-2 border border-[#0F2A43]/25 text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#EEF2F6] transition-all duration-300"
              >
                <MessageCircle size={13} /> WhatsApp
              </a>
            </div>
          </motion.div>
        )}

        {state === "ready" && booking && (
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease }}
              className="flex flex-col items-center text-center"
            >
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.55, delay: 0.15, ease }}
                className={`w-20 h-20 rounded-full flex items-center justify-center ${paid ? "bg-[#0F2A43]" : "bg-amber-100 border border-amber-300"}`}
                data-testid="bs-status-icon"
              >
                {paid ? <Check size={34} strokeWidth={2.5} className="text-white" /> : <Clock3 size={30} className="text-amber-600" />}
              </motion.span>
              <p className="mt-7 font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">DJI Avata 2 FPV Masterclass</p>
              <h1 className="mt-3 font-serif text-[38px] md:text-[54px] leading-[1.05] text-[#111827]" data-testid="bs-heading">
                {paid ? "Seat Confirmed." : "Booking Received."}
              </h1>
              <p className="mt-4 text-[#6B7280] leading-relaxed max-w-md">
                {paid
                  ? `Badhai ho ${booking.name.split(" ")[0]} — tumhari seat lock ho gayi hai. Details ka screenshot ya PDF save kar lena.`
                  : "Payment abhi confirm nahi hua hai. Agar amount debit ho gaya hai, WhatsApp par booking ID share karo."}
              </p>
            </motion.div>

            {/* Confirmation card (printable) */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease }}
              className="mt-12 bg-white rounded-[26px] border border-[#E5E7EB] shadow-[0_35px_80px_-30px_rgba(15,42,67,0.35)] overflow-hidden"
              data-testid="bs-card"
              id="bs-confirmation-card"
            >
              {/* card header */}
              <div className="bg-[#0F2A43] px-8 md:px-10 py-7 flex items-center justify-between">
                <div>
                  <p className="font-serif text-xl text-white tracking-wide">FILMMAKERAKKI</p>
                  <p className="mt-1 font-mono text-[8px] tracking-[0.3em] uppercase text-[#8FAECC]">FPV Masterclass · Booking Confirmation</p>
                </div>
                <Ticket size={26} strokeWidth={1.4} className="text-[#8FAECC]" />
              </div>

              {/* booking id banner */}
              <div className="px-8 md:px-10 py-6 bg-[#EEF2F6] border-b border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-[8px] tracking-[0.3em] uppercase text-[#6B7280]">Booking ID</p>
                  <p className="mt-1 font-mono font-bold text-xl tracking-[0.08em] text-[#0F2A43]" data-testid="bs-booking-id">{booking.booking_id}</p>
                </div>
                <span
                  className={`inline-flex items-center gap-2 font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full ${paid ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-amber-50 border border-amber-200 text-amber-700"}`}
                  data-testid="bs-status"
                >
                  <BadgeCheck size={13} /> {paid ? "Payment Confirmed" : "Payment Pending"}
                </span>
              </div>

              {/* details */}
              <div className="px-8 md:px-10 py-6">
                <Row label="Participant" value={booking.name} testid="bs-name" />
                <Row label="Batch" value={`${booking.batch} · ${booking.batch_note}`} testid="bs-batch" />
                <Row label="Plan" value={PLAN_LABELS[booking.plan] || booking.plan} testid="bs-plan" />
                <Row label="Amount Paid" value={`₹${(booking.amount / 100).toLocaleString("en-IN")}`} testid="bs-amount" />
                <Row
                  label="Booked On"
                  value={new Date(booking.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  testid="bs-date"
                />
              </div>

              {/* what's next */}
              <div className="px-8 md:px-10 pb-8">
                <div className="bg-[#F8F6F2] rounded-[18px] border border-[#E5E7EB] p-6">
                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#4F6D8A] flex items-center gap-2">
                    <CalendarRange size={13} /> What's Next
                  </p>
                  <ul className="mt-4 space-y-2.5">
                    {[
                      "WhatsApp group link 7 din pehle milega",
                      "Hotel + location details email par aayengi",
                      "Comfortable shoes aur apna creative energy laana",
                    ].map((t) => (
                      <li key={t} className="flex items-start gap-2.5 text-sm text-[#374151]">
                        <Check size={14} strokeWidth={2.5} className="text-[#4F6D8A] shrink-0 mt-0.5" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="mt-5 flex items-center gap-2 font-mono text-[8px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                  <ShieldCheck size={12} className="text-[#4F6D8A]" /> Yeh card tumhara entry pass hai — save kar lo
                </p>
              </div>
            </motion.div>

            {/* actions */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease }}
              className="mt-9 flex flex-wrap justify-center gap-4 print:hidden"
            >
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bs-whatsapp"
                className="inline-flex items-center justify-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#4F6D8A] hover:-translate-y-0.5 transition-all duration-300"
              >
                <MessageCircle size={13} /> Say Hi on WhatsApp
              </a>
              <button
                onClick={() => window.print()}
                data-testid="bs-print"
                className="inline-flex items-center justify-center gap-2 border border-[#0F2A43]/25 text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-white hover:-translate-y-0.5 transition-all duration-300"
              >
                <Printer size={13} /> Save as PDF
              </button>
              <Link
                to="/"
                data-testid="bs-home"
                className="inline-flex items-center justify-center gap-2 text-[#6B7280] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-6 py-4 rounded-full hover:text-[#0F2A43] transition-colors"
              >
                Back to Home
              </Link>
            </motion.div>
          </div>
        )}
      </section>

      <div className="print:hidden">
        <AeroFooter />
      </div>

      {/* print styles — sirf confirmation card print ho */}
      <style>{`
        @media print {
          body { background: #ffffff !important; }
          .grain-overlay { display: none !important; }
          main { padding: 0 !important; }
          section { padding-top: 0 !important; padding-bottom: 0 !important; }
        }
      `}</style>
    </motion.main>
  );
}
